import * as React from 'react';

import {
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  TextField,
  Divider,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

import { Orders, Devices, Labels } from '../../controllers';

const createUiId = () => {
    if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `bag-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getGtin = (qrCodeData = '') => {
    const match = String(qrCodeData).match(/\(01\)\s*([0-9]+)/);
    return match ? match[1] : '';
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const toWorkingBaggie = (label) => ({
    uiId: createUiId(),
    sourceId: label.id,
    data_general: clone(label.data_general || {}),
    gtin: getGtin(label.data_general?.qr_code_data),
    numberOfCopies: Number(label.data_general?.number_of_copies) > 0
        ? Number(label.data_general.number_of_copies)
        : 1,
    duplicated: false,
    sourceUiId: null
});

const toSavedWorkingBaggie = (saved, source) => {
    const bag = toWorkingBaggie(source);
    bag.data_general = {
        ...bag.data_general,
        type: saved.type,
        upper: saved.upper,
        lower: saved.lower,
        lot: saved.lot,
        ref: saved.ref
    };
    bag.gtin = saved.gtin;
    bag.numberOfCopies = Number(saved.numberOfCopies);
    bag.duplicated = true;
    return bag;
};

const toSavePayload = (bag) => ({
    sourceId: bag.sourceId,
    type: safeText(bag.data_general.type),
    upper: safeText(bag.data_general.upper),
    lower: safeText(bag.data_general.lower),
    lot: safeText(bag.data_general.lot),
    ref: safeText(bag.data_general.ref),
    gtin: safeText(bag.gtin),
    numberOfCopies: bag.numberOfCopies
});

const safeText = (value) => value === null || value === undefined ? '' : String(value);
const hasControlCharacter = (value) => Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
});

export default function Main() {
    const [printer, setPrinter] = React.useState(0);
    const [printers, setPrinters] = React.useState([]);
    const [orderId, setOrder] = React.useState('');
    const [orderInfo, setInfo] = React.useState(undefined);
    const [orderLabels, setLabels] = React.useState([]);
    const [workingBags, setWorkingBags] = React.useState([]);
    const [log, setLog] = React.useState(0);
    const [isPrinting, setIsPrinting] = React.useState(false);
    const [editingBag, setEditingBag] = React.useState(null);
    const [editingOriginal, setEditingOriginal] = React.useState(null);
    const [editError, setEditError] = React.useState('');
    const [deleteBag, setDeleteBag] = React.useState(null);
    const [previewBag, setPreviewBag] = React.useState(null);
    const [savedVersion, setSavedVersion] = React.useState(0);
    const [customized, setCustomized] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [restoreOpen, setRestoreOpen] = React.useState(false);

    React.useEffect(() => {
        fetchBaggers();
    }, []);

    React.useEffect(() => {
        if (orderInfo === undefined) {
            return;
        }
        getOrderLabels(orderInfo.id);
    }, [orderInfo]);

    const fetchBaggers = async () => {
        const response = await Devices.GetBaggers();
        if (!response || !response.success) {
            alert('Error Fetching printers');
            return;
        }
        setPrinters(response.result);
    };

    const handleFetch = async (event) => {
        event.preventDefault();
        if (isNaN(parseInt(orderId))) {
            alert('Invalid Input for order ID. \nEx: 1234567890');
            setOrder('');
            return;
        }

        setInfo(undefined);
        setLabels([]);
        setWorkingBags([]);
        setLog(0);
        setSavedVersion(0);
        setCustomized(false);
        setHasUnsavedChanges(false);
        const response = await Orders.GetById(orderId);
        if (!response || !response.success) {
            alert('Error Fetching order results');
            setOrder('');
            return;
        }

        setInfo(response.result);
        setOrder('');
    };

    const handlePrintRequest = async () => {
        if (printer < 1) {
            alert('Must select a target device');
            return;
        }
        if (workingBags.length < 1) {
            alert('The current print list is empty');
            return;
        }
        if (hasUnsavedChanges) {
            alert('Save the print list before printing');
            return;
        }
        if (isPrinting) {
            return;
        }

        setIsPrinting(true);
        const response = customized
            ? await Labels.PrintCustom(orderInfo.id, printer, savedVersion, createUiId())
            : await Labels.PrintRequest(orderInfo.id, printer);
        setIsPrinting(false);

        if (!response || !response.success) {
            alert(response?.message || 'Error while requesting print');
            return;
        }
        setLog(response.result);
    };

    const handleSinglePrintRequest = async (id) => {
        if (printer < 1) {
            alert('Must select a target device');
            return;
        }
        if (isPrinting) {
            return;
        }

        setIsPrinting(true);
        const response = await Labels.PrintRequestSingle(id, printer);
        setIsPrinting(false);
        if (!response || !response.success) {
            alert('Error while requesting print');
            return;
        }
        setLog(response.result);
    };

    const getOrderLabels = async (selectedOrderId) => {
        const response = await Labels.GetByOrder(selectedOrderId);
        if (!response || !response.success) {
            alert('No labels found for selected order');
            return;
        }

        setLabels(response.result);
        const savedResponse = await Labels.GetWorkingList(selectedOrderId);
        if (!savedResponse || !savedResponse.success) {
            alert(savedResponse?.message || 'Error loading saved print list');
            return;
        }

        const saved = savedResponse.result;
        const sourceMap = new Map(response.result.map((label) => [Number(label.id), label]));
        let loadedBags = response.result.map(toWorkingBaggie);
        if (saved.customized) {
            loadedBags = saved.bags.map((bag) => {
                const source = sourceMap.get(Number(bag.sourceId));
                return source ? toSavedWorkingBaggie(bag, source) : null;
            }).filter(Boolean);
            if (loadedBags.length !== saved.bags.length) {
                alert('A saved baggie no longer matches the original labels. Restore originals before printing');
            }
        }
        setWorkingBags(loadedBags);
        setSavedVersion(saved.version);
        setCustomized(saved.customized);
        setHasUnsavedChanges(false);
    };

    const openEdit = (bag) => {
        setEditingOriginal(clone(bag));
        setEditingBag(clone(bag));
        setEditError('');
    };

    const updateEditField = (field, value) => {
        setEditingBag((previous) => ({
            ...previous,
            data_general: {
                ...previous.data_general,
                [field]: value
            }
        }));
    };

    const validateEdit = () => {
        const fields = [
            ['Type', editingBag.data_general.type, 100],
            ['LOT', editingBag.data_general.lot, 100],
            ['REF', editingBag.data_general.ref, 100],
            ['Upper', editingBag.data_general.upper, 50],
            ['Lower', editingBag.data_general.lower, 50]
        ];
        for (const [name, value, max] of fields) {
            const text = safeText(value);
            if (['Type', 'LOT', 'REF'].includes(name) && text.trim() === '') {
                return `${name} is required`;
            }
            if (text.length > max || hasControlCharacter(text)) {
                return `${name} contains invalid characters or exceeds ${max} characters`;
            }
        }
        if (!editingBag.data_general.upper && !editingBag.data_general.lower) {
            return 'Upper and Lower cannot both be empty';
        }
        if (!/^\d{8,14}$/.test(safeText(editingBag.gtin))) {
            return 'GTIN must contain 8 to 14 digits';
        }
        if (
            safeText(editingBag.data_general.type) !== safeText(editingOriginal.data_general.type)
            && safeText(editingBag.gtin) === safeText(editingOriginal.gtin)
        ) {
            return 'Review and change GTIN when Type changes';
        }
        const copies = Number(editingBag.numberOfCopies);
        if (!Number.isInteger(copies) || copies < 1 || copies > 20) {
            return 'Number of copies must be an integer from 1 to 20';
        }
        return '';
    };

    const saveEdit = () => {
        const error = validateEdit();
        if (error) {
            setEditError(error);
            return;
        }
        setWorkingBags((previous) => previous.map((bag) =>
            bag.uiId === editingBag.uiId ? clone(editingBag) : bag
        ));
        setEditingBag(null);
        setEditingOriginal(null);
        setHasUnsavedChanges(true);
    };

    const duplicateBaggie = (bag) => {
        const copy = clone(bag);
        copy.uiId = createUiId();
        copy.duplicated = true;
        copy.sourceUiId = bag.uiId;
        setWorkingBags((previous) => {
            const index = previous.findIndex((item) => item.uiId === bag.uiId);
            const result = [...previous];
            result.splice(index + 1, 0, copy);
            return result;
        });
        setHasUnsavedChanges(true);
    };

    const confirmDelete = () => {
        setWorkingBags((previous) => previous.filter((bag) => bag.uiId !== deleteBag.uiId));
        setDeleteBag(null);
        setHasUnsavedChanges(true);
    };

    const handleSavePrintList = async () => {
        if (!orderInfo || isSaving || isPrinting) {
            return;
        }
        setIsSaving(true);
        const response = await Labels.SaveWorkingList(
            orderInfo.id,
            workingBags.map(toSavePayload),
            savedVersion,
            createUiId()
        );
        setIsSaving(false);
        if (!response || !response.success) {
            alert(response?.message || 'Error saving print list');
            return;
        }
        setSavedVersion(response.result.version);
        setCustomized(true);
        setHasUnsavedChanges(false);
    };

    const handleRestoreOriginals = async () => {
        if (!orderInfo || isSaving || isPrinting) {
            return;
        }
        if (!customized) {
            setWorkingBags(orderLabels.map(toWorkingBaggie));
            setHasUnsavedChanges(false);
            setRestoreOpen(false);
            return;
        }

        setIsSaving(true);
        const response = await Labels.RestoreWorkingList(orderInfo.id, savedVersion, createUiId());
        setIsSaving(false);
        if (!response || !response.success) {
            alert(response?.message || 'Error restoring original labels');
            return;
        }
        setWorkingBags(orderLabels.map(toWorkingBaggie));
        setSavedVersion(response.result.version);
        setCustomized(false);
        setHasUnsavedChanges(false);
        setRestoreOpen(false);
    };

    return (
        <Box sx={{ marginTop: 1, display: 'flex', flexDirection: 'column', padding: 2 }}>
            <Typography component="h1" variant="h5">Selected Printer</Typography>
            <Divider sx={{ minWidth: '100%' }} />
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }}>
                <Select
                    sx={{ width: '100%' }}
                    id="kind"
                    name="kind"
                    value={printer}
                    onChange={(event) => setPrinter(event.target.value)}
                    size="small"
                >
                    <MenuItem key={0} value={0} disabled>Printer List</MenuItem>
                    {printers.map((value) => (
                        <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                    ))}
                </Select>
            </Box>

            <br />
            <Typography component="h1" variant="h5">Scan Order ID</Typography>
            <Divider sx={{ minWidth: '100%' }} />
            <Box sx={{ mt: 1 }} component="form" onSubmit={handleFetch}>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Enter Order ID"
                    id="order"
                    name="order"
                    autoFocus
                    value={orderId}
                    onChange={(event) => setOrder(event.target.value)}
                />
            </Box>

            <br />
            {(orderInfo && Object.keys(orderInfo).length > 0) && (
                <Box>
                    <Typography component="h1" variant="h5">Order Info</Typography>
                    <Divider sx={{ minWidth: '100%' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        <TextField margin="normal" fullWidth label="ORDER ID" value={orderInfo.id} InputProps={{ readOnly: true }} />
                        <TextField margin="normal" fullWidth label="CASE ID" value={orderInfo.case_serial} InputProps={{ readOnly: true }} />
                        <TextField margin="normal" fullWidth label="PART COUNT" value={orderInfo.parts} InputProps={{ readOnly: true }} />
                    </Box>
                    <TextField margin="normal" fullWidth label="PRINT REQUEST ID" value={log} InputProps={{ readOnly: true }} />
                    <Button
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={handlePrintRequest}
                        disabled={isPrinting || isSaving || workingBags.length === 0 || hasUnsavedChanges}
                    >
                        {isPrinting ? 'Sending Print Request...' : 'Print'}
                    </Button>
                </Box>
            )}

            <br />
            {orderInfo && (
                <Box>
                    <Typography component="h1" variant="h5">Current Print List</Typography>
                    <Divider sx={{ minWidth: '100%' }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSavePrintList}
                            disabled={!hasUnsavedChanges || isSaving || isPrinting}
                        >
                            {isSaving ? 'Saving...' : 'Save Print List'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<RestoreIcon />}
                            onClick={() => setRestoreOpen(true)}
                            disabled={(!customized && !hasUnsavedChanges) || isSaving || isPrinting}
                        >
                            Restore Originals
                        </Button>
                        <Typography sx={{ alignSelf: 'center' }} color={hasUnsavedChanges ? 'warning.main' : 'text.secondary'}>
                            {hasUnsavedChanges
                                ? 'Unsaved changes — PRINT is disabled'
                                : customized
                                    ? `Saved shared version #${savedVersion}`
                                    : `Original labels${savedVersion > 0 ? ` — version #${savedVersion}` : ''}`}
                        </Typography>
                    </Stack>
                    {workingBags.length === 0 && (
                        <Typography sx={{ padding: 2 }} color="text.secondary">No baggies in the current print list.</Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', padding: 2 }}>
                        {workingBags.map((bag, index) => (
                            <Card sx={{ boxShadow: 5, minWidth: 230 }} key={bag.uiId}>
                                <Box sx={{ display: 'flex', padding: 0.5 }}>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => openEdit(bag)} disabled={isPrinting || isSaving}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Duplicate">
                                        <IconButton size="small" onClick={() => duplicateBaggie(bag)} disabled={isPrinting || isSaving}><ContentCopyIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => setDeleteBag(bag)} disabled={isPrinting || isSaving}><DeleteIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Preview">
                                        <IconButton size="small" onClick={() => setPreviewBag(bag)} disabled={isPrinting}><VisibilityIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </Box>
                                <CardContent sx={{ paddingTop: 0 }}>
                                    <Typography variant="h5">Label #{index + 1}</Typography>
                                    <Typography variant="body2">Type: {bag.data_general.type}</Typography>
                                    <Typography variant="body2">Steps: {safeText(bag.data_general.upper)} - {safeText(bag.data_general.lower)}</Typography>
                                    <Typography variant="body2">Copies: {bag.numberOfCopies}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            {orderLabels.length > 0 && (
                <Box>
                    <Typography component="h1" variant="h5">Manual Reprint (Original Labels)</Typography>
                    <Divider sx={{ minWidth: '100%' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', padding: 2 }}>
                        {orderLabels.map((label, index) => (
                            <Card sx={{ boxShadow: 5 }} key={label.id}>
                                <CardActionArea onClick={() => handleSinglePrintRequest(label.id)} disabled={isPrinting || isSaving} sx={{ height: '100%' }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h5">Label #{index + 1}</Typography>
                                        <Typography variant="body2">Type: {label.data_general.type}</Typography>
                                        <Typography variant="body2">Steps: {label.data_general.upper} - {label.data_general.lower}</Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            <Dialog open={Boolean(editingBag)} onClose={() => !isPrinting && !isSaving && setEditingBag(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Baggie</DialogTitle>
                {editingBag && (
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField label="Patient" value={safeText(editingBag.data_general.patient)} InputProps={{ readOnly: true }} />
                            <TextField label="Doctor" value={safeText(editingBag.data_general.doctor)} InputProps={{ readOnly: true }} />
                            <TextField label="Manufacturer" value={safeText(editingBag.data_general.manufacturer)} InputProps={{ readOnly: true }} />
                            <TextField label="Type" value={safeText(editingBag.data_general.type)} onChange={(event) => updateEditField('type', event.target.value)} />
                            <TextField label="Upper" value={safeText(editingBag.data_general.upper)} onChange={(event) => updateEditField('upper', event.target.value)} />
                            <TextField label="Lower" value={safeText(editingBag.data_general.lower)} onChange={(event) => updateEditField('lower', event.target.value)} />
                            <TextField label="LOT" value={safeText(editingBag.data_general.lot)} onChange={(event) => updateEditField('lot', event.target.value)} />
                            <TextField label="REF" value={safeText(editingBag.data_general.ref)} onChange={(event) => updateEditField('ref', event.target.value)} />
                            <TextField label="GTIN" value={safeText(editingBag.gtin)} onChange={(event) => setEditingBag((previous) => ({ ...previous, gtin: event.target.value }))} helperText="Required when Type changes; used in QR segment (01)." />
                            <TextField label="Number of copies" type="number" value={editingBag.numberOfCopies} inputProps={{ min: 1, max: 20 }} onChange={(event) => setEditingBag((previous) => ({ ...previous, numberOfCopies: Number(event.target.value) }))} />
                            {editError && <Typography color="error">{editError}</Typography>}
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => { setEditingBag(null); setEditingOriginal(null); }}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteBag)} onClose={() => setDeleteBag(null)}>
                <DialogTitle>Remove Baggie</DialogTitle>
                <DialogContent>
                    <Typography>Remove this baggie from the current print list?</Typography>
                    {deleteBag && <Typography sx={{ mt: 1 }}>Type: {deleteBag.data_general.type} — Steps: {safeText(deleteBag.data_general.upper)} - {safeText(deleteBag.data_general.lower)}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteBag(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>Remove</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(previewBag)} onClose={() => setPreviewBag(null)} fullWidth maxWidth="sm">
                <DialogTitle>Baggie Preview</DialogTitle>
                {previewBag && (
                    <DialogContent>
                        <Stack spacing={1}>
                            <Typography>Patient: {safeText(previewBag.data_general.patient)}</Typography>
                            <Typography>Doctor: {safeText(previewBag.data_general.doctor)}</Typography>
                            <Typography>REF: {safeText(previewBag.data_general.ref)}</Typography>
                            <Typography>Type: {safeText(previewBag.data_general.type)}</Typography>
                            <Typography>Upper / Lower: {safeText(previewBag.data_general.upper)} - {safeText(previewBag.data_general.lower)}</Typography>
                            <Typography>LOT: {safeText(previewBag.data_general.lot)}</Typography>
                            <Typography>GTIN: {safeText(previewBag.gtin)}</Typography>
                            <Typography color="text.secondary">QR will be regenerated and validated when PRINT is requested.</Typography>
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions><Button onClick={() => setPreviewBag(null)}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={restoreOpen} onClose={() => !isSaving && setRestoreOpen(false)}>
                <DialogTitle>Restore Original Baggies</DialogTitle>
                <DialogContent>
                    <Typography>
                        Restore the original baggies for this order? The saved versions will remain in history, but all computers will return to the original labels.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreOpen(false)} disabled={isSaving}>Cancel</Button>
                    <Button color="warning" variant="contained" onClick={handleRestoreOriginals} disabled={isSaving}>
                        Restore Originals
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
